import React, { useEffect, useState } from 'react';
import { useSecurityStatus } from '../../hooks/security';
import { Link, useHistory } from 'react-router-dom';
// Style
import '../../scss/app.scss';
import { getOperatorConfigs, handleChange, handleError, isProd, trimField } from '../Shared/Functions';
import { Button, CircularProgress, TextField } from '@mui/material';
import { ForgotUsername } from './ForgotUsername';
import { ForgotPassword } from './ForgotPassword';
import ReCAPTCHA from "react-google-recaptcha";
import { CaptchaService } from '../../services/V2/keymaster';
import MigrateUser from './MigrateUser';
import DOMPurify from 'dompurify';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const LoginForm = ({fail, success}) => {
    const auth: any = useSecurityStatus();
    const recaptchaRef: any = React.useRef();
    const history = useHistory();

    const [submitting, setSubmitting] = useState<boolean>(false);
    const [setToken, setSetToken] = useState<any>();
    const [showForgotUsername, setShowForgotUsername] = useState<boolean>(false);
    const [showForgotPassword, setShowForgotPassword] = useState<boolean>(false);
    const [allowGuestPayments, setAllowGuestPayments] = useState<boolean>(false);
    const [showUserMigrate, setShowUserMigrate] = useState<boolean>(false);
    const [portalGreeting, setPortalGreeting] = useState<any>();
    const [login, setLogin] = useState<any>({
        username: "",
        password: ""
    })
    const [showPassword, setShowPassword] = useState<boolean>(false);

    useEffect(() => {
        getOperatorConfigs()
            .then(x => {
                setAllowGuestPayments(x?.allowGuestPayments ?? false)
                setPortalGreeting(x.greetings)
            })
    }, [])

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true)
        var token = setToken;
        if(!setToken){
            await recaptchaRef?.current?.reset();
            token = await recaptchaRef?.current?.executeAsync()
            setSetToken(token);
        }
        CaptchaService.captchaValidate(token)
            .then(x => {
                if(x.success) {
                    auth.login(login)
                        .then(x => {})
                        .catch(x => {
                            setSubmitting(false);
                            x.body.Message === "User must go through password migration." ? setShowUserMigrate(true) :
                            x.message !=="Not Found" && fail(handleError(x))
                            setSetToken(null)
                        })
                } else {
                    setSubmitting(false)
                    setSetToken(null)
                    fail("An error occurred validating the reCaptcha. Please refresh the page to try again. If errors persist, please contact support.")
                }
            })
    }

    const sanitizedGreeting = () => ({
        __html: DOMPurify.sanitize(portalGreeting),
    });

    return <>
        {showForgotUsername &&
            <ForgotUsername hide={() => setShowForgotUsername(false)} show={showForgotUsername} fail={fail} success={success} />
        }
        {showForgotPassword &&
            <ForgotPassword hide={() => setShowForgotPassword(false)} show={showForgotPassword} fail={fail} success={success} />
        }
        {showUserMigrate &&
            <MigrateUser hide={() => setShowUserMigrate(false)} show={showUserMigrate} fail={fail} success={success} username={login.username} />
        }
        <div className='portal-greeting' dangerouslySetInnerHTML={sanitizedGreeting()} />
        <form className="login-form" style={{ width: "100%" }}>
            <ReCAPTCHA
                ref={recaptchaRef}
                size="invisible"
                sitekey={isProd() ? "6LdysiEhAAAAAPZwsLD0CCf4U8yJvrD6pkuGuTHw" : "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"}
            />
            <TextField
                disabled={submitting}
                onChange={e => handleChange(e, login, setLogin)}
                onBlur={e => trimField(e, login, setLogin)}
                margin="normal"
                name="username"
                label="Username"
                type="text"
                autoComplete="username"
                value={login.username}
                fullWidth
            />
            <TextField
                disabled={submitting}
                onChange={e => handleChange(e, login, setLogin)}
                onBlur={e => trimField(e, login, setLogin)}
                margin="normal"
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={login.password}
                fullWidth
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                aria-label="toggle password visibility"
                                onClick={() => setShowPassword(!showPassword)}
                                onMouseDown={e => e.preventDefault()}
                                edge="end"
                                >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </InputAdornment>
                    )
                }}
            />
            <Button variant="contained"
                className="login-button"
                disabled={submitting}
                id="submit"
                style={{width:"100%"}}
                type="submit"
                color="primary"
                onClick={submit}>
                {auth.authenticating ?
                    <>
                        <div className='loading-container'><CircularProgress className='center-spinner' color="primary" aria-label="Loading" /></div> {' Signing in...'}
                    </> : 'Sign In'}
            </Button>
            <nav style={{marginTop: "15px"}} aria-label="Help links">
                <ul style={{listStyle: "none", padding: 0, margin: 0, display: "flex", gap: "0.5em"}}>
                    <li>Forgot your&nbsp;<Link to="#" onClick={() => setShowForgotUsername(true)} style={{color: '#1565c0'}}>Username</Link></li>
                    <li>or</li>
                    <li><Link to="#" onClick={() => setShowForgotPassword(true)} style={{color: '#1565c0'}}>Password?</Link></li>
                </ul>
            </nav>
            <br />
            <Button variant="outlined"
                className="login-button"
                disabled={submitting}
                id="submit"
                style={{width:"100%"}}
                type="submit"
                color="primary"
                onClick={() => history.push('/sign-up')}>
                Sign Up
            </Button>
            {allowGuestPayments &&
                <Button variant="outlined"
                    className="login-button"
                    disabled={submitting}
                    id="submit"
                    style={{width:"100%", marginTop: "10px"}}
                    type="submit"
                    color="primary"
                    onClick={() => history.push('/guest-pay')}>
                    Guest Pay
                </Button>
            }
        </form>
    </>
}

export default LoginForm;